<template>
  <div class="position-relative">
    <DataTable
      ref="transactionsTable"
      :value="rows"
      filterDisplay="row"
      dataKey="key"
      class="transactions-datatable border rounded"
      v-bind="virtualScrollProps"
      :rowClass="rowClass"
    >
      <template #empty>
        <div class="text-center py-5">
          <FontAwesomeIcon
            :icon="['far', 'inbox']"
            size="3x"
            class="text-muted mb-3"
          />
          <h4 class="text-muted">
            {{ $t("company_orders.transactions.no_transactions") }}
          </h4>
        </div>
      </template>

      <Column
        v-if="isAdmin"
        header="Order ID"
        :showFilterMenu="false"
        :headerStyle="{ width: '5rem' }"
      >
        <template #body="{ data }">
          <span
            v-if="data.kind === 'order'"
            class="alert alert-subtle-warning text-center admin-extra-info px-2 py-1 d-inline-block mb-0"
            >{{ data.order.id }}</span
          >
        </template>
      </Column>

      <Column
        v-if="showCompany"
        :header="$t('common.company')"
        :showFilterMenu="false"
      >
        <template #body="{ data }">
          <a
            v-if="isAdmin && data.kind === 'order'"
            href="#"
            class="alert alert-subtle-warning text-decoration-none admin-extra-info p-1 d-inline-block mb-0"
            @click.prevent="$emit('manage:company', data.order)"
          >
            {{ data.order.company_name }}
            <FontAwesomeIcon
              :icon="['far', 'arrow-up-right-from-square']"
              class="ms-1"
              size="xs"
            />
          </a>
          <template v-else>{{ data.companyName ?? "-" }}</template>
        </template>
        <template #filter>
          <InputText
            v-model="filters.companyName"
            type="text"
            class="p-column-filter w-100"
            :placeholder="$t('common.filter')"
          />
        </template>
      </Column>

      <Column
        :header="$t('company_orders.transactions.transaction_type')"
        :showFilterMenu="false"
      >
        <template #body="{ data }">
          <div class="d-flex align-items-center">
            <FontAwesomeIcon :icon="data.icon" class="me-2" />
            <span class="text-truncate" :title="data.typeLabel">
              {{ data.typeLabel }}
            </span>
          </div>
        </template>
        <template #filter>
          <MultiSelect
            v-model="filters.types"
            :options="typeOptions"
            optionLabel="label"
            optionValue="value"
            :placeholder="$t('common.filter')"
            class="p-column-filter w-100"
            display="chip"
            appendTo="body"
            :focusOnHover="false"
          >
            <template #value="{ value, placeholder }">
              <span v-if="value && value.length" class="text-primary fw-bold">
                {{ $t("dashboard.portfolio.table.filters.active") }}
              </span>
              <span v-else>{{ placeholder }}</span>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <Column :header="$t('company_orders.portfolio')" :showFilterMenu="false">
        <template #body="{ data }">
          <span class="d-block text-truncate" :title="data.portfolioName">
            {{ data.portfolioName ?? "-" }}
          </span>
        </template>
        <template #filter>
          <MultiSelect
            v-model="filters.portfolios"
            :options="portfolioOptions"
            :placeholder="$t('common.filter')"
            class="p-column-filter w-100"
            display="chip"
            appendTo="body"
            filter
            :focusOnHover="false"
          >
            <template #value="{ value, placeholder }">
              <span v-if="value && value.length" class="text-primary fw-bold">
                {{ $t("dashboard.portfolio.table.filters.active") }}
              </span>
              <span v-else>{{ placeholder }}</span>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <Column
        :header="$t('company_orders.total_selected_mwh')"
        :showFilterMenu="false"
        :headerStyle="{ width: '6rem' }"
      >
        <template #body="{ data }">
          <span class="fw-semibold">{{ formatMwh(data.volume) }}</span>
          MWh
        </template>
      </Column>

      <Column
        :header="$t('company_orders.initiated_by')"
        :showFilterMenu="false"
      >
        <template #body="{ data }">
          <span
            class="d-block text-truncate"
            :title="data.kind === 'order' ? data.order.order_starter_name : ''"
          >
            {{
              data.kind === "order"
                ? (data.order.order_starter_name ?? "-")
                : "-"
            }}
          </span>
        </template>
        <template #filter>
          <InputText
            v-model="filters.starter"
            type="text"
            class="p-column-filter w-100"
            :placeholder="$t('common.filter')"
          />
        </template>
      </Column>

      <Column :showFilterMenu="false" :headerStyle="{ width: '11rem' }">
        <template #header>
          <button
            type="button"
            class="btn btn-link p-0 text-reset fw-bold text-decoration-none"
            @click="toggleSortOrder"
          >
            {{ $t("company_orders.date") }}
            <FontAwesomeIcon
              :icon="getSortIcon(sortOrder === 'asc' ? 1 : -1)"
              class="pv-datatable-sort-icon ms-1"
            />
          </button>
        </template>
        <template #body="{ data }">
          {{ formatRowDate(data) }}
        </template>
        <template #filter>
          <DateRangePickerComponent
            v-model="pickedRange"
            class="p-column-filter w-100"
            @select="applyPickedRange"
            @clear="$emit('reset:date-range')"
          />
        </template>
      </Column>

      <Column
        :header="$t('common.status')"
        :showFilterMenu="false"
        :headerStyle="{ width: '9rem' }"
      >
        <template #body="{ data }">
          <CompanyOrderStatusBadge
            v-if="data.kind === 'order'"
            :status="data.order.status"
          />
        </template>
        <template #filter>
          <MultiSelect
            v-model="filters.statuses"
            :options="statusOptions"
            optionLabel="label"
            optionValue="value"
            :placeholder="$t('common.filter')"
            class="p-column-filter w-100"
            display="chip"
            appendTo="body"
            :focusOnHover="false"
          >
            <template #value="{ value, placeholder }">
              <span v-if="value && value.length" class="text-primary fw-bold">
                {{ $t("dashboard.portfolio.table.filters.active") }}
              </span>
              <span v-else>{{ placeholder }}</span>
            </template>
            <template #option="slotProps">
              <CompanyOrderStatusBadge :status="slotProps.option.value" />
            </template>
          </MultiSelect>
        </template>
      </Column>

      <Column
        :header="$t('common.actions.title')"
        :showFilterMenu="false"
        :headerStyle="{ width: isAdmin ? '8rem' : '5.5rem' }"
      >
        <template #body="{ data }">
          <div class="d-flex gap-1 flex-nowrap align-items-center">
            <button
              type="button"
              class="btn btn-sm btn-icon btn-outline-primary"
              :title="$t('company_orders.view')"
              @click="viewRow(data)"
            >
              <FontAwesomeIcon :icon="['fas', 'eye']" />
            </button>

            <OrderActionsDropdown
              v-if="data.kind === 'order'"
              :order="data.order"
              :busy="isOrderBusy(data.order)"
              :spinning="isOrderSpinning(data.order)"
              @confirm="$emit('confirm:order', data.order)"
              @reject="$emit('reject:order', data.order)"
            />

            <AdminOrderActionsDropdown
              v-if="isAdmin && data.kind === 'order'"
              :order="data.order"
              :busy="companyOrderStore.isUpdatingStatus === data.order.id"
              @update:status="
                (status) => $emit('admin:status', data.order, status)
              "
              @retry="$emit('admin:retry', data.order)"
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <ScrollMoreIndicator
      :scrollTarget="scrollElement"
      :itemHeight="ROW_HEIGHT_PX"
    >
      <template #default="{ count }">
        ~{{ $t("company_orders.transactions.more_transactions", { count }) }}
      </template>
    </ScrollMoreIndicator>

    <div v-if="isLoadingMore" class="d-flex justify-content-center mt-2">
      <FontAwesomeIcon icon="spinner" class="fa-spin text-muted" />
    </div>
  </div>
</template>

<script setup lang="ts">
import DateRangePickerComponent from "@/components/common/DateRangePickerComponent.vue";
import type { DateRange } from "@/components/common/dateRange";
import ScrollMoreIndicator from "@/components/common/ScrollMoreIndicator.vue";
import { formatDateTime, formatMwh } from "@/composables/formatUtils";
import {
  useVirtualScrolledTable,
  type VirtualScrolledTable,
} from "@/composables/useVirtualScrolledTable";
import { getSortIcon } from "@/utils/sortIcons";
import { useInfiniteScroll } from "@vueuse/core";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import { computed, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { CompanyOrder } from "../common/companyOrderTypes";
import { OrderStatus, OrderType } from "../common/companyOrderTypes";
import { useCompanyOrderStore } from "../common/useCompanyOrderStore";
import AdminOrderActionsDropdown from "./AdminOrderActionsDropdown.vue";
import CompanyOrderStatusBadge from "./CompanyOrderStatusBadge.vue";
import OrderActionsDropdown from "./OrderActionsDropdown.vue";
import {
  eventTypeFilterValue,
  orderTypeFilterValue,
  type TransactionFilters,
} from "./transactionFilters";
import {
  EVENT_ROW_TYPES,
  type RegistryEventSummary,
  type SortOrder,
} from "./transactionLogTypes";
import type { EventGroupRow, TransactionRow } from "./transactionRows";
import {
  formatEventRowType,
  formatOrderType,
} from "./transactionTypePresentation";

const ROW_HEIGHT_PX = 48;
const LOAD_MORE_DISTANCE_PX = ROW_HEIGHT_PX * 10;

/**
 * Emits `load:more` while `canLoadMore` holds and the rows don't fill the scroll viewport, and again
 * as the user scrolls near its end.
 *
 * @property loadedRowCount - Orders plus events loaded, before filtering; sets the virtual scrolling
 *   mode so a filter never switches it
 * @property canLoadMore - Another page exists and no request is in flight
 */
const props = defineProps<{
  rows: TransactionRow[];
  loadedRowCount: number;
  isAdmin: boolean;
  showCompany: boolean;
  hasLinkedEvents: boolean;
  portfolioOptions: string[];
  canLoadMore: boolean;
  isLoadingMore: boolean;
}>();

const emit = defineEmits<{
  "view:order": [order: CompanyOrder];
  "view:event": [event: RegistryEventSummary];
  "view:group": [group: EventGroupRow];
  "confirm:order": [order: CompanyOrder];
  "reject:order": [order: CompanyOrder];
  "admin:status": [order: CompanyOrder, status: "COMPLETED" | "CANCELLED"];
  "admin:retry": [order: CompanyOrder];
  "manage:company": [order: CompanyOrder];
  "reset:date-range": [];
  "load:more": [];
}>();

const filters = defineModel<TransactionFilters>("filters", { required: true });
const sortOrder = defineModel<SortOrder>("sortOrder", { required: true });
const dateRange = defineModel<[from: Date, to: Date] | null>("dateRange", {
  required: true,
});

const pickedRange = ref<DateRange | null>(dateRange.value);

const { t } = useI18n();
const companyOrderStore = useCompanyOrderStore();
const transactionsTable =
  useTemplateRef<VirtualScrolledTable>("transactionsTable");
const {
  tableProps: virtualScrollProps,
  resetScroll,
  scrollElement,
} = useVirtualScrolledTable(
  transactionsTable,
  () => props.loadedRowCount,
  ROW_HEIGHT_PX
);
const { reset: recheckLoadMore } = useInfiniteScroll(
  scrollElement,
  () => emit("load:more"),
  {
    distance: LOAD_MORE_DISTANCE_PX,
    canLoadMore: () => props.canLoadMore,
  }
);

const typeOptions = computed(() => [
  ...Object.values(OrderType).map((type) => ({
    label: formatOrderType(type, t),
    value: orderTypeFilterValue(type),
  })),
  ...(props.hasLinkedEvents
    ? EVENT_ROW_TYPES.map((type) => ({
        label: formatEventRowType(type, t),
        value: eventTypeFilterValue(type),
      }))
    : []),
]);

const statusOptions = computed(() =>
  Object.values(OrderStatus).map((status) => ({
    label: t(`company_orders.status.${status.toLowerCase()}`),
    value: status,
  }))
);

watch(dateRange, (range) => {
  pickedRange.value = range;
});

watch([filters, sortOrder, dateRange], resetScroll, { deep: true });

watch(() => props.rows.length, recheckLoadMore);

const toggleSortOrder = () => {
  sortOrder.value = sortOrder.value === "desc" ? "asc" : "desc";
};

const applyPickedRange = () => {
  const [from, to] = pickedRange.value ?? [];
  if (from && to) dateRange.value = [from, to];
};

const rowClass = (row: TransactionRow) => {
  if (row.kind !== "order") return "transaction-event-row";
  return row.order.status === OrderStatus.CANCELLED
    ? "transaction-inactive-row"
    : "";
};

const formatRowDate = (row: TransactionRow): string => {
  const at = formatDateTime(row.at);
  if (row.kind !== "event_group" || at === formatDateTime(row.endedAt)) {
    return at;
  }
  return `${at}–${formatTime(row.endedAt)}`;
};

const formatTime = (value: string): string =>
  new Date(value).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });

const isOrderBusy = (order: CompanyOrder) =>
  companyOrderStore.isConfirming === order.id ||
  companyOrderStore.isBatchProcessing;

const isOrderSpinning = (order: CompanyOrder) =>
  companyOrderStore.isConfirming === order.id ||
  companyOrderStore.batchProcessingOrderId === order.id;

const viewRow = (row: TransactionRow) => {
  if (row.kind === "order") emit("view:order", row.order);
  else if (row.kind === "event") emit("view:event", row.event);
  else emit("view:group", row);
};
</script>

<style scoped lang="scss">
.transactions-datatable {
  :deep(.pv-datatable-container) {
    overflow-x: auto;
  }

  :deep(.pv-datatable-body-row) {
    height: v-bind("`${ROW_HEIGHT_PX}px`");
    white-space: nowrap;
  }

  :deep(.transaction-event-row > td) {
    background: var(--phoenix-gray-50);
  }

  :deep(.transaction-inactive-row > td) {
    color: var(--phoenix-tertiary-color);
  }
}
</style>
